import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Turnstile } from '@/components/Turnstile';
import { authClient } from '@/lib/auth';

// Early access: signup requires a code from the site owner, checked up
// front (step 1 below) before either signup path (email/password or
// Google) is offered. Flip this off once early access ends — see
// backend/auth/index.ts and backend/routes/auth.ts for the matching
// server-side gate that also needs relaxing at that point.
const EARLY_ACCESS = true;

export function SignupPage() {
  // Step 1 (only when EARLY_ACCESS): verify the access code before
  // showing either signup path. Once EARLY_ACCESS is off, skip straight
  // to step 2 — there's nothing to gate.
  const [codeVerified, setCodeVerified] = useState(!EARLY_ACCESS);
  const [signupCode, setSignupCode] = useState('');
  const [codeTurnstileToken, setCodeTurnstileToken] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    setVerifyingCode(true);
    try {
      // Non-consuming check — it just unlocks step 2. The code itself
      // gets consumed later, on whichever signup path the user actually
      // completes (email/password below, or Google — the server stashes
      // the code in a short-lived cookie for that path, see
      // backend/routes/auth.ts's verify-signup-code handler).
      const res = await fetch('/api/auth/verify-signup-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ signupCode, turnstileToken: codeTurnstileToken }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        setCodeError(body?.error ?? `Invalid code (${res.status})`);
        return;
      }
      setCodeVerified(true);
    } catch (err) {
      setCodeError(err instanceof Error ? err.message : 'Could not verify code');
    } finally {
      setVerifyingCode(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Better Auth's standard signUp doesn't support extra fields on its fetch;
      // use raw fetch so we can include the turnstile token and signup code.
      const res = await fetch('/api/auth/sign-up/email', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password, name, turnstileToken, signupCode }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        setError(body?.error ?? body?.message ?? `Sign up failed (${res.status})`);
        return;
      }
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function signInWith(provider: 'github' | 'google') {
    setError(null);
    try {
      // The signup code (already verified in step 1) travels via the
      // pending_signup_code cookie set by /api/auth/verify-signup-code —
      // databaseHooks.user.create.before (backend/auth/index.ts) reads
      // and consumes it when the OAuth callback actually creates the
      // account. GitHub isn't wired up on that path yet, so its button
      // is left out below.
      await authClient.signIn.social({ provider, callbackURL: '/dashboard' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'OAuth redirect failed');
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Check your email</CardTitle>
            <CardDescription>
              We sent a verification link to <strong>{email}</strong>. Click it to finish creating
              your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login" className="text-sm underline-offset-4 hover:underline">
              Back to sign in
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (EARLY_ACCESS && !codeVerified) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Create a Pure account</CardTitle>
            <CardDescription>We&apos;re in early access — enter your code to continue.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={onVerifyCode} className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="signupCode">Access code</Label>
                <Input
                  id="signupCode"
                  value={signupCode}
                  onChange={(e) => setSignupCode(e.target.value)}
                  placeholder="XXXX-XXXX"
                  required
                />
              </div>
              <Turnstile onToken={setCodeTurnstileToken} />
              {codeError && (
                <Alert variant="destructive">
                  <AlertDescription>{codeError}</AlertDescription>
                </Alert>
              )}
              <Button type="submit" className="w-full" disabled={verifyingCode}>
                {verifyingCode ? 'Checking…' : 'Continue'}
              </Button>
            </form>
            <div className="text-sm text-center">
              Already have an account?{' '}
              <Link to="/login" className="font-medium underline-offset-4 hover:underline">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create a Pure account</CardTitle>
          <CardDescription>Start compiling your code in seconds</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button variant="outline" className="w-full" onClick={() => signInWith('google')} type="button">
            Continue with Google
          </Button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or with email</span>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                minLength={10}
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">At least 10 characters.</p>
            </div>
            <Turnstile onToken={setTurnstileToken} />
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create account'}
            </Button>
          </form>
          <div className="text-sm text-center">
            Already have an account?{' '}
            <Link to="/login" className="font-medium underline-offset-4 hover:underline">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
